import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2 } from "lucide-react";

/**
 * Renders a standard legal document (privacy policy or T&C) fetched
 * from `/api/legal/{kind}`. The backend auto-fills the tenant's brand
 * name, slug, and support email so every tenant gets a compliant page
 * with their details without any per-tenant content authoring.
 */
export default function LegalPage({ kind }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/legal/${kind}`)
      .then(({ data }) => { if (alive) setDoc(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, pathname]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 flex items-center gap-2 text-sm text-[hsl(var(--kb-muted))]">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (error || !doc) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-2xl font-semibold mb-2">Page unavailable</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))]">{error || "Failed to load."}</p>
      </div>
    );
  }

  return (
    <article
      className="max-w-3xl mx-auto px-6 py-12 prose prose-neutral prose-sm"
      data-testid={`legal-${kind}-page`}>
      <header className="mb-10 border-b border-[hsl(var(--kb-border))] pb-6">
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="legal-title">
          {doc.title}
        </h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2">
          {doc.brand_name} · Effective {doc.effective_date}
        </p>
      </header>

      {doc.sections.map((sec, i) => (
        <section key={i} className="mb-10">
          <h2 className="text-xl font-semibold tracking-tight mt-0 mb-3"
              data-testid={`legal-section-${i}`}>
            {sec.heading}
          </h2>
          {(sec.paragraphs || []).map((p, j) => (
            <p key={j} className="leading-relaxed mb-3 text-sm">
              {_renderInline(p)}
            </p>
          ))}
          {(sec.bullets || []).length > 0 && (
            <ul className="list-disc pl-6 mb-3 text-sm space-y-1.5">
              {sec.bullets.map((b, j) => (
                <li key={j}>{_renderInline(b)}</li>
              ))}
            </ul>
          )}
          {(sec.extra_paragraphs || []).map((p, j) => (
            <p key={`x-${j}`} className="leading-relaxed mb-3 text-sm">
              {_renderInline(p)}
            </p>
          ))}
        </section>
      ))}

      <footer className="mt-12 pt-6 border-t border-[hsl(var(--kb-border))] text-xs text-[hsl(var(--kb-muted))]">
        Need help? Reach us at <a href={`mailto:${doc.support_email}`} className="underline">{doc.support_email}</a>.
      </footer>
    </article>
  );
}

// Render `markdown-style` `inline code` segments without pulling in a parser.
function _renderInline(text) {
  if (typeof text !== "string") return text;
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) =>
    p.startsWith("`") && p.endsWith("`")
      ? <code key={i} className="text-xs bg-[hsl(var(--kb-surface-2))] px-1.5 py-0.5 rounded">{p.slice(1, -1)}</code>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}
